/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import G17Client from '../../services/G17Client';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { SurveyRange, CustomInitiatives, CustomReport, CustomReportType, MetricType } from '../../types/custom-report';
import CustomReportsList from './CustomReportsList';
import CustomReportManage from './CustomReportManage';
import CustomReportsBreadcrumbs from './CustomReportsBreadcrumbs';
import Dashboard, { DashboardRow } from '../dashboard';
import CustomReportMetricAddForm from './CustomReportMetricAddForm';
import CustomReportMetricAddDisambiguation from './CustomReportMetricAddDisambiguation';
import { rootAppPath, getRootAppPath } from '../../routes/company-tracker/utils';
import { RouteInterfaceMin } from '../../types/routes';
import { ROUTES } from '../../constants/routes';
import CustomReportCreateDisambiguation from './CustomReportCreateDisambiguation';
import './styles.scss';
import CustomReportManageSubsidiary from './CustomReportManageSubsidiary';
import CustomReportManageDate from './CustomReportManageDate';
import Loader from '../loader';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { UpgradeRequired } from '../../routes/upgrade-required';

const downloadsRoot = `/${rootAppPath}/downloads/:initiativeId?/custom`;
const commonRoute: RouteInterfaceMin = {
  getRootAppPath,
  path: ''
}

export const CustomReportRoutes: { [key: string]: RouteInterfaceMin } = {
  ROOT: {
    ...commonRoute,
    path: `${downloadsRoot}/manage`
  },
  CREATE_REPORT: {
    ...commonRoute,
    path: `${downloadsRoot}/create/:createType?`
  },
  MANAGE_REPORT: {
    ...commonRoute,
    path: `${downloadsRoot}/manage/:reportId`
  },
  ADD_METRIC: {
    ...commonRoute,
    path: `${downloadsRoot}/manage/:reportId/add/:metricType?`
  },
}

const CustomReportsRoot = () => {

  const dispatch = useAppDispatch();
  const reportMatch = useRouteMatch<{
    initiativeId?: string,
    reportId?: string
  }>(`${downloadsRoot}/:_(view|manage)/:reportId`);

  const reportId = reportMatch?.params.reportId;

  const [customReport, setCustomReport] = useState<CustomReport | undefined>();
  const initiativeState = useAppSelector((state) => state.initiative);
  const canAccessCombinedReport = useAppSelector(FeaturePermissions.canAccessCombinedReport);
  const [loading, setLoading] = useState(false);

  const handleReload = useCallback(async () => {
    if (!initiativeState.loaded) {
      return Promise.resolve();
    }

    if (!reportId) {
      setCustomReport(undefined);
      return Promise.resolve();
    }

    setLoading(true);
    return G17Client.getCustomReport(initiativeState.id, reportId)
      .then((report) => {
        setCustomReport(report);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        dispatch(addSiteAlert({
          content: 'Failed to fetch the list of custom reports. Please try again later',
          color: SiteAlertColors.Danger,
        }))
      });
  }, [initiativeState, reportId, dispatch]);

  React.useEffect(() => {
    handleReload();
  }, [reportId, handleReload]);

  const ReportManager = () => {
    switch (customReport?.type) {
      case CustomReportType.Initiatives:
        return (
          <CustomReportManageSubsidiary customReport={customReport as CustomInitiatives} handleReload={handleReload} />
        );
      case CustomReportType.Survey:
      case CustomReportType.SurveyAggregation:
        return <CustomReportManageDate customReport={customReport as SurveyRange} handleReload={handleReload} />;
      case CustomReportType.Metrics:
      default:
        return <CustomReportManage customReport={customReport} handleReload={handleReload} />;
    }
  }

  if (!canAccessCombinedReport) {
    return <UpgradeRequired />
  }

  if (loading) {
    return <Loader />
  }

  return (
    <Dashboard className='custom-report-root'>
      <DashboardRow>
        <Route path={`${ROUTES.DOWNLOADS_CUSTOM.path}/:action?/:metricType?`}>
          <CustomReportsBreadcrumbs customReport={customReport} />
        </Route>
      </DashboardRow>
      {!customReport
        ?
        <Switch>
          <Route path={CustomReportRoutes.CREATE_REPORT.path}>
            <CustomReportCreateDisambiguation handleReload={handleReload} />
          </Route>
          <Route>
            <CustomReportsList />
          </Route>
        </Switch>
        :
        <Switch>
          <Route path={CustomReportRoutes.ADD_METRIC.path} render={(props) => {
            if (props.match.params.metricType) {
              return (
                <CustomReportMetricAddForm
                  type={props.match.params.metricType as MetricType}
                  customReport={customReport}
                  handleReload={handleReload}
                />
              );
            }
            return <CustomReportMetricAddDisambiguation customReport={customReport} />
          }} />
          <Route path={CustomReportRoutes.MANAGE_REPORT.path}>
            <ReportManager />
          </Route>
        </Switch>
      }
    </Dashboard>
  );
}

export default CustomReportsRoot;
